import type { AccountRelatedData } from "@/features/accounts/types";
import { buildAppliedAmountMap } from "@/lib/credit-application-balances";
import { roundToCents } from "@/lib/money";

function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/**
 * What is left on each credit, keyed by credit id.
 *
 * A credit's face value stops describing it the moment any of it is applied, so
 * the Credits tab reports both. Applications net their own reversals, and the
 * result floors at zero: an over-applied credit has nothing left to spend, not
 * a negative amount to read as a debt.
 */
export function buildCreditAvailableBalances(
  credits: readonly AccountRelatedData["credits"][number][],
  applications: readonly AccountRelatedData["credit_applications"][number][],
): Map<string, number> {
  const appliedByCredit = buildAppliedAmountMap(applications, "credit_id");

  return new Map(
    credits.map((credit) => [
      credit.id,
      Math.max(0, roundToCents(toNumber(credit.amount) - (appliedByCredit.get(credit.id) ?? 0))),
    ]),
  );
}

/**
 * The account-level credit balance across all credits.
 *
 * Ledger formula, unfloored: amount - applied + reversed, per
 * openspec/specs/credit-applications/spec.md ("Balance Calculation") and
 * get_account_credit_balance. Flooring each credit at zero (what the per-credit
 * display balance above does) made this surface disagree with the credits
 * list, which never floored, and hid an over-applied credit behind a
 * healthy-looking total.
 */
export function computeAccountCreditBalance(
  credits: readonly AccountRelatedData["credits"][number][],
  applications: readonly AccountRelatedData["credit_applications"][number][],
): number {
  const appliedByCredit = buildAppliedAmountMap(applications, "credit_id");

  return credits.reduce((sum, credit) => {
    const creditAmount = toNumber(credit.amount);
    const appliedAmount = appliedByCredit.get(credit.id) ?? 0;
    return roundToCents(sum + roundToCents(creditAmount - appliedAmount));
  }, 0);
}
